import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Plus } from "lucide-react";
import { VehicleModelList } from "@/components/vehicle-models/VehicleModelList";
import { VehicleModelModal } from "@/components/vehicle-models/VehicleModelModal";
import {
  useVehicleModels,
  useDeleteVehicleModel,
} from "@/hooks/useVehicleModels";
import { VehicleModel } from "@/types";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { loginRedirectState } from "@/utils/auth";

export default function VehicleModelsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">(
    "create",
  );
  const [selectedVehicleModel, setSelectedVehicleModel] = useState<
    VehicleModel | undefined
  >();
  const [showDeleteConfirm, setShowDeleteConfirm] =
    useState<VehicleModel | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMaker, setSelectedMaker] = useState("");
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(["pages", "common"]);

  const {
    data: vehicleModelsData,
    isLoading,
    error,
  } = useVehicleModels({
    search: searchTerm || undefined,
    maker: selectedMaker || undefined,
    year: selectedYear,
    orderBy: "year",
    orderDirection: "desc",
  });

  const deleteVehicleModelMutation = useDeleteVehicleModel();

  const vehicleModels = vehicleModelsData?.vehicleModels || [];

  const handleCreateVehicleModel = () => {
    if (!authLoading && !user) {
      navigate("/login", { state: loginRedirectState(location) });
      return;
    }

    setSelectedVehicleModel(undefined);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleEditVehicleModel = (vehicleModel: VehicleModel) => {
    setSelectedVehicleModel(vehicleModel);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleViewVehicleModel = (vehicleModel: VehicleModel) => {
    setSelectedVehicleModel(vehicleModel);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleDeleteVehicleModel = (vehicleModel: VehicleModel) => {
    setShowDeleteConfirm(vehicleModel);
  };

  const confirmDelete = async () => {
    if (showDeleteConfirm) {
      try {
        await deleteVehicleModelMutation.mutateAsync(showDeleteConfirm.id);
        setShowDeleteConfirm(null);
      } catch (error) {
        console.error("Error deleting vehicle model:", error);
      }
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is handled by the query parameters, so this just prevents form submission
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedMaker("");
    setSelectedYear(undefined);
  };

  // Get unique makers for filter dropdown
  const uniqueMakers = Array.from(
    new Set(vehicleModels.map((vm) => vm.maker)),
  ).sort();

  if (error) {
    return (
 <div className="page-container">
 <div className="bg-red-50 border border-red-200 rounded-lg p-4">
  <h3 className="text-red-800 font-medium">
            {t("pages:vehicles.models.errorLoading")}
           </h3>
  <p className="text-red-600 mt-1">
             {error instanceof Error
               ? error.message
               : t("pages:vehicles.models.unknownError")}
           </p>
        </div>
      </div>
    );
  }

  return (
 <div className="page-container">
 <div className="flex justify-between items-center mb-3 sm:mb-8">
        <div>
  <h1 className="page-title">{t("pages:vehicles.models.pageTitle")}</h1>
  <p className="page-subtitle">{t("pages:vehicles.models.pageSubtitle")}</p>
        </div>

        <button
          onClick={handleCreateVehicleModel}
 className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
        >
  <Plus className="h-4 w-4" />
           {t("pages:vehicles.models.addVehicleModel")}
         </button>
      </div>

      {/* Search and Filter Controls */}
 <div className="bg-white rounded-lg border border-secondary-200 p-6 mb-6">
 <form onSubmit={handleSearch} className="space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <div className="md:col-span-2">
  <label className="block text-sm font-medium text-secondary-700 mb-2">
                 {t("pages:vehicles.models.fields.search")}
               </label>
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                   placeholder={t("pages:vehicles.models.searchPlaceholder")}
 className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
  <label className="block text-sm font-medium text-secondary-700 mb-2">
                 {t("pages:vehicles.models.fields.maker")}
               </label>
              <select
                value={selectedMaker}
                onChange={(e) => setSelectedMaker(e.target.value)}
 className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                 <option value="">{t("pages:vehicles.models.allMakers")}</option>
                {uniqueMakers.map((maker) => (
                  <option key={maker} value={maker}>
                    {maker}
                  </option>
                ))}
              </select>
            </div>

            <div>
  <label className="block text-sm font-medium text-secondary-700 mb-2">
                 {t("pages:vehicles.models.fields.year")}
               </label>
              <input
                type="number"
                value={selectedYear || ""}
                onChange={(e) =>
                  setSelectedYear(parseInt(e.target.value) || undefined)
                }
                 placeholder={t("pages:vehicles.models.anyYearPlaceholder")}
                min="1900"
                max={new Date().getFullYear() + 5}
 className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

 <div className="flex justify-end">
            <button
              type="button"
              onClick={clearFilters}
               className="px-4 py-2 text-secondary-600 hover:text-secondary-800 transition-colors"
             >
               {t("pages:vehicles.models.clearFilters")}
             </button>
          </div>
        </form>
      </div>

      {/* Results Summary */}
 <div className="mb-6">
 <p className="text-sm text-secondary-600">
           {isLoading
             ? t("common:loading")
             : t("pages:vehicles.models.showingCount", {
                 count: vehicleModels.length,
               })}
        </p>
      </div>

      <VehicleModelList
        vehicleModels={vehicleModels}
        isLoading={isLoading}
        onEdit={handleEditVehicleModel}
        onDelete={handleDeleteVehicleModel}
        onView={handleViewVehicleModel}
         emptyMessage={t("pages:vehicles.models.defaultEmptyMessage")}
      />

      {/* Vehicle Model Modal */}
      <VehicleModelModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        vehicleModel={selectedVehicleModel}
        mode={modalMode}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
  <h3 className="text-lg font-semibold text-secondary-900 mb-4">
               {t("pages:vehicles.models.deleteTitle")}
             </h3>
  <p className="text-secondary-600 mb-6">
               {t("pages:vehicles.models.deleteMessage", {
                 name: `${showDeleteConfirm.year} ${showDeleteConfirm.maker} ${showDeleteConfirm.model}`,
               })}
             </p>
 <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
 className="px-4 py-2 border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50 transition-colors"
                 disabled={deleteVehicleModelMutation.isPending}
               >
                 {t("common:cancel")}
               </button>
              <button
                onClick={confirmDelete}
                disabled={deleteVehicleModelMutation.isPending}
 className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                 {deleteVehicleModelMutation.isPending
                   ? t("pages:sessions.deleting")
                   : t("common:delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
